// src/pages/MappaInterventi.jsx
// Mappa d'Italia degli interventi e delle manutenzioni, con la stima dei
// costi di trasferta (carburante, usura del furgone, ore di manodopera),
// il confronto con quanto e' stato fatturato per capire su quali clienti
// si guadagna e su quali si rimette, e la classifica mensile dei tecnici.
import React, { useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, CircleMarker, Tooltip as MapTooltip, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { authFetch } from '../utils/authFetch';
import { Card } from '../components/ui';

const API_BASE = '/api';

// colori di stato (margine positivo / negativo / sconosciuto) e serie unica
const COLORE_POSITIVO = '#0ca30c';
const COLORE_NEGATIVO = '#d03b3b';
const COLORE_NEUTRO = '#6b7280';
const COLORE_SERIE = '#2a78d6';

const CARBURANTI = ['diesel', 'benzina', 'gpl', 'metano', 'ibrido', 'elettrico'];
const MESI = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];

// --- formattazione ------------------------------------------
const euro = (n, cifre = 0) =>
  n === null || n === undefined || Number.isNaN(Number(n))
    ? '—'
    : Number(n).toLocaleString('it-IT', { minimumFractionDigits: cifre, maximumFractionDigits: cifre }) + ' €';

const num = (n, cifre = 1) =>
  n === null || n === undefined || Number.isNaN(Number(n))
    ? '—'
    : Number(n).toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: cifre });

const dataIt = (d) => {
  if (!d) return '—';
  const x = new Date(d);
  return Number.isNaN(x.getTime()) ? '—' : x.toLocaleDateString('it-IT');
};

const dataIso = (d) => {
  if (!d) return '';
  const x = new Date(d);
  if (Number.isNaN(x.getTime())) return '';
  const mm = String(x.getMonth() + 1).padStart(2, '0');
  const gg = String(x.getDate()).padStart(2, '0');
  return `${x.getFullYear()}-${mm}-${gg}`;
};

const chiaveMese = (d) => {
  const x = new Date(d);
  if (Number.isNaN(x.getTime())) return '';
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}`;
};

const etichettaMese = (chiave) => {
  if (!chiave) return '';
  const [anno, mese] = chiave.split('-');
  return `${MESI[Number(mese) - 1] || mese} ${anno}`;
};

const coloreMargine = (m) => (m === null || m === undefined ? COLORE_NEUTRO : m >= 0 ? COLORE_POSITIVO : COLORE_NEGATIVO);

const somma = (arr, f) => arr.reduce((acc, x) => acc + (Number(f(x)) || 0), 0);
const sommaONull = (arr, f) => {
  const validi = arr.map(f).filter((v) => v !== null && v !== undefined && !Number.isNaN(Number(v)));
  return validi.length ? validi.reduce((a, b) => a + Number(b), 0) : null;
};

// --- chiamate ------------------------------------------------
async function chiamata(url, options) {
  const res = await authFetch(url, options);
  if (!res) throw new Error('Sessione scaduta.');
  const dati = await res.json().catch(() => null);
  if (!res.ok) throw new Error(dati?.error || 'Errore del server.');
  return dati;
}

const json = (body, method = 'POST') => ({
  method,
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
});

// ============================================================
//  COMPONENTI DI SUPPORTO
// ============================================================

const Tile = ({ etichetta, valore, nota, colore }) => (
  <Card className="shadow-sm p-4">
    <div className="text-xs uppercase tracking-wide text-gray-500">{etichetta}</div>
    <div className="text-2xl font-semibold text-gray-800 mt-1" style={colore ? { color: colore } : undefined}>
      {valore}
    </div>
    {nota && <div className="text-xs text-gray-500 mt-1">{nota}</div>}
  </Card>
);

const Th = ({ children, className = '' }) => (
  <th className={`px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide whitespace-nowrap ${className}`}>
    {children}
  </th>
);
const Td = ({ children, className = '' }) => (
  <td className={`px-3 py-2 text-sm text-gray-700 whitespace-nowrap ${className}`}>{children}</td>
);

const Margine = ({ valore }) => (
  <span className="font-semibold" style={{ color: coloreMargine(valore) }}>
    {euro(valore)}
  </span>
);

const inputCls =
  'mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';
const btnPrimario = 'px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium transition disabled:opacity-50';
const btnSecondario = 'px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium transition disabled:opacity-50';

// Leaflet misura il contenitore una volta sola, al montaggio: se la pagina
// cambia larghezza dopo (caricamento, cambio scheda, rotazione del tablet)
// le tile restano in una striscia. Qui si rimisura a ogni ridimensionamento.
function RidimensionaMappa() {
  const map = useMap();
  useEffect(() => {
    const contenitore = map.getContainer();
    const rimisura = () => map.invalidateSize();
    const timer = setTimeout(rimisura, 50);
    let osservatore = null;
    if (typeof ResizeObserver !== 'undefined') {
      osservatore = new ResizeObserver(rimisura);
      osservatore.observe(contenitore);
    }
    window.addEventListener('resize', rimisura);
    return () => {
      clearTimeout(timer);
      if (osservatore) osservatore.disconnect();
      window.removeEventListener('resize', rimisura);
    };
  }, [map]);
  return null;
}

// Modale per correggere posizione e dati di trasferta di una lavorazione
function EditorTrasferta({ lavorazione, veicoli, tecnici, onChiudi, onSalvato }) {
  const t = lavorazione.trasferta || {};
  const [form, setForm] = useState({
    lat: lavorazione.posizione?.lat ?? '',
    lng: lavorazione.posizione?.lng ?? '',
    tecnicoId: t.tecnicoId || lavorazione.tecnicoId || '',
    data: dataIso(t.data || lavorazione.dataIntervento),
    oraInizio: t.oraInizio || '',
    oraFine: t.oraFine || '',
    veicoloId: t.veicoloId || '',
    km: t.km ?? '',
  });
  const [salvando, setSalvando] = useState(false);
  const [errore, setErrore] = useState('');

  const cambia = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const geocodifica = async () => {
    setErrore('');
    try {
      const pos = await chiamata(`${API_BASE}/geocodifica`, json({ indirizzo: lavorazione.place }));
      setForm((p) => ({ ...p, lat: pos.lat, lng: pos.lng }));
    } catch (err) {
      setErrore(err.message);
    }
  };

  const salva = async () => {
    setSalvando(true);
    setErrore('');
    try {
      const posizione =
        form.lat === '' || form.lng === '' ? null : { lat: Number(form.lat), lng: Number(form.lng) };
      await chiamata(
        `${API_BASE}/work/${lavorazione._id}/trasferta`,
        json(
          {
            posizione,
            trasferta: {
              tecnicoId: form.tecnicoId || null,
              data: form.data || null,
              oraInizio: form.oraInizio,
              oraFine: form.oraFine,
              veicoloId: form.veicoloId || null,
              km: form.km === '' ? null : Number(form.km),
            },
          },
          'PATCH'
        )
      );
      onSalvato();
    } catch (err) {
      setErrore(err.message);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1000] bg-black/40 flex items-center justify-center p-4" onClick={onChiudi}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">
              {lavorazione.internalCode || 'Lavorazione'} · {lavorazione.companyName || '—'}
            </h3>
            <p className="text-sm text-gray-500">{lavorazione.place || 'luogo non indicato'}</p>
          </div>
          <button type="button" className={btnSecondario} onClick={onChiudi}>
            Chiudi
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600">Latitudine</label>
            <input name="lat" value={form.lat} onChange={cambia} className={inputCls} placeholder="41.9028" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600">Longitudine</label>
            <input name="lng" value={form.lng} onChange={cambia} className={inputCls} placeholder="12.4964" />
          </div>
          <div className="col-span-2 flex items-end">
            <button type="button" className={btnSecondario} onClick={geocodifica} disabled={!lavorazione.place}>
              Trova dal luogo
            </button>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600">Tecnico</label>
            <select name="tecnicoId" value={form.tecnicoId} onChange={cambia} className={inputCls}>
              <option value="">—</option>
              {tecnici.map((u) => (
                <option key={u._id} value={u._id}>
                  {u.nome}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600">Data intervento</label>
            <input type="date" name="data" value={form.data} onChange={cambia} className={inputCls} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600">Ora inizio</label>
            <input type="time" name="oraInizio" value={form.oraInizio} onChange={cambia} className={inputCls} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600">Ora fine</label>
            <input type="time" name="oraFine" value={form.oraFine} onChange={cambia} className={inputCls} />
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-medium text-gray-600">Veicolo</label>
            <select name="veicoloId" value={form.veicoloId} onChange={cambia} className={inputCls}>
              <option value="">— predefinito —</option>
              {veicoli.map((v) => (
                <option key={v._id} value={v._id}>
                  {v.nome} {v.targa ? `(${v.targa})` : ''} {v.attivo ? '' : '· archiviato'}
                </option>
              ))}
            </select>
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-medium text-gray-600">Km percorsi A/R (vuoto = stima dalla mappa)</label>
            <input type="number" min="0" name="km" value={form.km} onChange={cambia} className={inputCls} />
          </div>
        </div>

        {errore && <p className="text-sm text-red-600 mt-3">{errore}</p>}

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" className={btnSecondario} onClick={onChiudi}>
            Annulla
          </button>
          <button type="button" className={btnPrimario} onClick={salva} disabled={salvando}>
            {salvando ? 'Salvataggio…' : 'Salva'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Tabella delle lavorazioni con costi, ricavo e margine
function TabellaLavorazioni({ righe, onModifica }) {
  if (!righe.length) return <p className="text-sm text-gray-500">Nessuna lavorazione.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <Th>Codice</Th>
            <Th>Tipo</Th>
            <Th>Data</Th>
            <Th>Cliente</Th>
            <Th>Luogo</Th>
            <Th>Tecnico</Th>
            <Th className="text-right">Ore</Th>
            <Th className="text-right">Km</Th>
            <Th className="text-right">Costo</Th>
            <Th className="text-right">Ricavo</Th>
            <Th className="text-right">Margine</Th>
            <Th></Th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {righe.map((l) => (
            <tr key={l._id} className="hover:bg-gray-50">
              <Td className="font-medium">{l.internalCode || '—'}</Td>
              <Td>{l.type === 'manutenzione' ? 'Manut.' : 'Interv.'}</Td>
              <Td>{dataIt(l.dataIntervento)}</Td>
              <Td className="max-w-[16rem] truncate">{l.companyName || '—'}</Td>
              <Td className="max-w-[16rem] truncate" title={l.place}>
                {l.posizione ? '' : '⚠ '}
                {l.place || '—'}
              </Td>
              <Td>{l.tecnicoNome || '—'}</Td>
              <Td className="text-right">{num(l.ore)}</Td>
              <Td className="text-right">
                {num(l.km, 0)}
                {l.kmStimati && l.km !== null ? <span className="text-gray-400" title="stimati dalla mappa">*</span> : null}
              </Td>
              <Td className="text-right">{euro(l.costi?.totale)}</Td>
              <Td className="text-right">{euro(l.ricavo)}</Td>
              <Td className="text-right">
                <Margine valore={l.margine} />
              </Td>
              <Td>
                <button type="button" className={btnSecondario} onClick={() => onModifica(l)}>
                  Modifica
                </button>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================
//  PAGINA
// ============================================================

export default function MappaInterventi() {
  const annoCorrente = new Date().getFullYear();
  const [anno, setAnno] = useState(String(annoCorrente));
  const [dati, setDati] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errore, setErrore] = useState('');

  const [tab, setTab] = useState('mappa');
  const [tipo, setTipo] = useState('tutti');
  const [tecnicoFiltro, setTecnicoFiltro] = useState('tutti');
  const [luogoSelezionato, setLuogoSelezionato] = useState(null);
  const [inModifica, setInModifica] = useState(null);
  const [meseTecnici, setMeseTecnici] = useState('');
  const [geocodificaStato, setGeocodificaStato] = useState('');

  const carica = async () => {
    setLoading(true);
    setErrore('');
    try {
      const d = await chiamata(`${API_BASE}/statistiche-interventi?anno=${encodeURIComponent(anno)}`);
      setDati(d);
    } catch (err) {
      setErrore(err.message || 'Errore nel caricamento.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carica();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anno]);

  // --- filtri ----------------------------------------------
  const lavorazioni = useMemo(() => {
    let lista = dati?.lavorazioni || [];
    if (tipo !== 'tutti') lista = lista.filter((l) => l.type === tipo);
    if (tecnicoFiltro !== 'tutti') lista = lista.filter((l) => String(l.tecnicoId || '') === tecnicoFiltro);
    return lista;
  }, [dati, tipo, tecnicoFiltro]);

  const idsFiltrati = useMemo(() => new Set(lavorazioni.map((l) => String(l._id))), [lavorazioni]);

  const uscite = useMemo(
    () => (dati?.uscite || []).filter((u) => u.lavorazioni.some((id) => idsFiltrati.has(String(id)))),
    [dati, idsFiltrati]
  );

  // --- aggregazioni ----------------------------------------
  const aggrega = (gruppo) => ({
    n: gruppo.length,
    ore: sommaONull(gruppo, (l) => l.ore),
    km: sommaONull(gruppo, (l) => l.km),
    carburante: sommaONull(gruppo, (l) => l.costi?.carburante),
    usura: sommaONull(gruppo, (l) => l.costi?.usura),
    manodopera: sommaONull(gruppo, (l) => l.costi?.manodopera),
    costo: somma(gruppo, (l) => l.costi?.totale),
    ricavo: sommaONull(gruppo, (l) => l.ricavo),
    margine: sommaONull(gruppo, (l) => l.margine),
    conRicavo: gruppo.filter((l) => l.ricavo !== null && l.ricavo !== undefined).length,
    lavorazioni: gruppo,
  });

  const luoghi = useMemo(() => {
    const mappa = new Map();
    for (const l of lavorazioni) {
      if (!l.posizione) continue;
      const k = `${l.posizione.lat.toFixed(3)},${l.posizione.lng.toFixed(3)}`;
      if (!mappa.has(k)) mappa.set(k, []);
      mappa.get(k).push(l);
    }
    return [...mappa.entries()].map(([k, gruppo]) => {
      const nomi = {};
      for (const l of gruppo) nomi[l.companyName || '—'] = (nomi[l.companyName || '—'] || 0) + 1;
      const cliente = Object.entries(nomi).sort((a, b) => b[1] - a[1])[0][0];
      return { key: k, lat: gruppo[0].posizione.lat, lng: gruppo[0].posizione.lng, cliente, place: gruppo[0].place, ...aggrega(gruppo) };
    });
  }, [lavorazioni]);

  const clienti = useMemo(() => {
    const mappa = new Map();
    for (const l of lavorazioni) {
      const k = (l.companyName || '—').trim().toLowerCase();
      if (!mappa.has(k)) mappa.set(k, []);
      mappa.get(k).push(l);
    }
    return [...mappa.values()]
      .map((gruppo) => ({ cliente: gruppo[0].companyName || '—', ...aggrega(gruppo) }))
      .sort((a, b) => (a.margine ?? Infinity) - (b.margine ?? Infinity));
  }, [lavorazioni]);

  const senzaPosizione = useMemo(() => lavorazioni.filter((l) => !l.posizione), [lavorazioni]);

  const totali = useMemo(() => aggrega(lavorazioni), [lavorazioni]);

  // classifica tecnici per mese (dalle uscite: le ore contano una volta per uscita)
  const mesi = useMemo(() => {
    const perMese = new Map();
    for (const u of dati?.uscite || []) {
      const m = chiaveMese(u.data);
      if (!m) continue;
      if (!perMese.has(m)) perMese.set(m, new Map());
      const perTecnico = perMese.get(m);
      const k = u.tecnicoId || '?';
      if (!perTecnico.has(k)) {
        perTecnico.set(k, { tecnicoId: k, nome: u.tecnicoNome || 'Non indicato', ore: 0, uscite: 0, km: 0, rapportini: 0, senzaOrari: 0 });
      }
      const r = perTecnico.get(k);
      r.uscite += 1;
      r.rapportini += u.lavorazioni.length;
      r.km += Number(u.km) || 0;
      if (u.ore === null || u.ore === undefined) r.senzaOrari += 1;
      else r.ore += Number(u.ore);
    }
    const out = {};
    for (const [m, perTecnico] of perMese) {
      out[m] = [...perTecnico.values()].sort((a, b) => b.ore - a.ore || b.rapportini - a.rapportini);
    }
    return out;
  }, [dati]);

  const chiaviMesi = useMemo(() => Object.keys(mesi).sort().reverse(), [mesi]);

  useEffect(() => {
    if (!meseTecnici || !mesi[meseTecnici]) setMeseTecnici(chiaviMesi[0] || '');
  }, [chiaviMesi, mesi, meseTecnici]);

  const classificaAnno = useMemo(() => {
    const perTecnico = new Map();
    for (const righe of Object.values(mesi)) {
      for (const r of righe) {
        if (!perTecnico.has(r.tecnicoId)) perTecnico.set(r.tecnicoId, { ...r, mesiVinti: 0 });
        else {
          const acc = perTecnico.get(r.tecnicoId);
          acc.ore += r.ore;
          acc.uscite += r.uscite;
          acc.km += r.km;
          acc.rapportini += r.rapportini;
          acc.senzaOrari += r.senzaOrari;
        }
      }
      if (righe[0] && righe[0].ore > 0) perTecnico.get(righe[0].tecnicoId).mesiVinti += 1;
    }
    return [...perTecnico.values()].sort((a, b) => b.ore - a.ore || b.rapportini - a.rapportini);
  }, [mesi]);

  // --- azioni ----------------------------------------------
  const geocodificaMancanti = async () => {
    setGeocodificaStato('Geocodifica in corso…');
    try {
      let giri = 0;
      let totRisolte = 0;
      let rimanenti = null;
      while (giri < 40) {
        giri += 1;
        // eslint-disable-next-line no-await-in-loop
        const r = await chiamata(`${API_BASE}/work/geocodifica-mancanti`, json({ limite: 10 }));
        totRisolte += r.risolte;
        rimanenti = r.rimanenti;
        setGeocodificaStato(`Trovate ${totRisolte} posizioni, ancora ${rimanenti} luoghi da cercare…`);
        if (!r.elaborate || r.rimanenti === 0) break;
      }
      setGeocodificaStato(
        rimanenti === 0
          ? `Fatto: ${totRisolte} posizioni trovate.`
          : `Trovate ${totRisolte} posizioni. ${rimanenti} luoghi non sono stati riconosciuti: correggili a mano con "Modifica".`
      );
      await carica();
    } catch (err) {
      setGeocodificaStato(`Errore: ${err.message}`);
    }
  };

  const dopoSalvataggio = async () => {
    setInModifica(null);
    await carica();
  };

  // --- render ----------------------------------------------
  const tabs = [
    ['mappa', 'Mappa'],
    ['clienti', 'Clienti'],
    ['tecnici', 'Tecnici'],
    ['lavorazioni', 'Lavorazioni'],
    ['impostazioni', 'Costi e veicoli'],
  ];

  const luogoAttivo = luogoSelezionato ? luoghi.find((l) => l.key === luogoSelezionato) : null;
  const anniDisponibili = dati?.anniDisponibili?.length ? dati.anniDisponibili : [annoCorrente];
  if (!anniDisponibili.includes(Number(anno)) && anno !== 'tutti') anniDisponibili.unshift(Number(anno));

  return (
    <div className="min-h-screen bg-gray-100 pb-10">
      <div className="max-w-7xl mx-auto px-4 pt-8">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-1">Mappa interventi e costi</h1>
            <p className="text-sm text-gray-500">
              Dove siamo andati, quanto ci e' costato andarci e quanto abbiamo fatturato. I km con l'asterisco
              sono stimati dalla distanza in linea d'aria dalla sede.
            </p>
          </div>

          <div className="flex flex-wrap gap-2 items-end">
            <div>
              <label className="block text-xs font-medium text-gray-600">Anno</label>
              <select value={anno} onChange={(e) => setAnno(e.target.value)} className={inputCls}>
                {anniDisponibili.map((a) => (
                  <option key={a} value={String(a)}>
                    {a}
                  </option>
                ))}
                <option value="tutti">Tutti</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600">Tipo</label>
              <select value={tipo} onChange={(e) => setTipo(e.target.value)} className={inputCls}>
                <option value="tutti">Tutti</option>
                <option value="intervento">Interventi</option>
                <option value="manutenzione">Manutenzioni</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600">Tecnico</label>
              <select value={tecnicoFiltro} onChange={(e) => setTecnicoFiltro(e.target.value)} className={inputCls}>
                <option value="tutti">Tutti</option>
                {(dati?.tecnici || []).map((u) => (
                  <option key={u._id} value={String(u._id)}>
                    {u.nome}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {errore && <div className="mb-4 rounded-lg bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">{errore}</div>}

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
          <Tile etichetta="Lavorazioni" valore={totali.n} nota={`${uscite.length} uscite`} />
          <Tile etichetta="Ore lavorate" valore={num(totali.ore)} nota="dagli orari sui verbali" />
          <Tile etichetta="Km percorsi" valore={num(totali.km, 0)} />
          <Tile etichetta="Costi trasferta" valore={euro(totali.costo)} nota="carburante + usura + ore" />
          <Tile etichetta="Fatturato" valore={euro(totali.ricavo)} nota={`${totali.conRicavo} con importo`} />
          <Tile etichetta="Margine" valore={euro(totali.margine)} colore={coloreMargine(totali.margine)} nota="solo con importo" />
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          {tabs.map(([k, label]) => (
            <button
              key={k}
              type="button"
              onClick={() => setTab(k)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                tab === k ? 'bg-emerald-600 text-white' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {loading && <p className="text-sm text-gray-500 mb-4">Caricamento…</p>}

        {/* ================= MAPPA ================= */}
        {tab === 'mappa' && (
          <div className="space-y-4">
            <Card className="shadow-sm overflow-hidden">
              <div style={{ height: 560 }}>
                <MapContainer center={[42.3, 12.5]} zoom={6} style={{ height: '100%', width: '100%' }} scrollWheelZoom>
                  <RidimensionaMappa />
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  {dati?.impostazioni?.sede?.lat != null && dati?.impostazioni?.sede?.lng != null && (
                    <CircleMarker
                      center={[dati.impostazioni.sede.lat, dati.impostazioni.sede.lng]}
                      radius={7}
                      pathOptions={{ color: '#111827', fillColor: '#111827', fillOpacity: 1, weight: 2 }}
                    >
                      <MapTooltip>Sede</MapTooltip>
                    </CircleMarker>
                  )}
                  {luoghi.map((l) => (
                    <CircleMarker
                      key={l.key}
                      center={[l.lat, l.lng]}
                      radius={Math.min(6 + Math.sqrt(l.n) * 3, 26)}
                      pathOptions={{
                        color: '#ffffff',
                        weight: luogoSelezionato === l.key ? 3 : 1.5,
                        fillColor: coloreMargine(l.margine),
                        fillOpacity: 0.85,
                      }}
                      eventHandlers={{ click: () => setLuogoSelezionato(l.key) }}
                    >
                      <MapTooltip>
                        <div className="text-sm">
                          <div className="font-semibold">{l.cliente}</div>
                          <div>{l.n} lavorazioni · {num(l.ore)} h · {num(l.km, 0)} km</div>
                          <div>
                            costi {euro(l.costo)} · fatturato {euro(l.ricavo)} · margine {euro(l.margine)}
                          </div>
                        </div>
                      </MapTooltip>
                    </CircleMarker>
                  ))}
                </MapContainer>
              </div>
              <div className="px-4 py-2 flex flex-wrap gap-4 text-xs text-gray-600 border-t border-gray-200">
                <span className="flex items-center gap-1">
                  <span className="inline-block w-3 h-3 rounded-full" style={{ background: COLORE_POSITIVO }} /> margine positivo
                </span>
                <span className="flex items-center gap-1">
                  <span className="inline-block w-3 h-3 rounded-full" style={{ background: COLORE_NEGATIVO }} /> margine negativo
                </span>
                <span className="flex items-center gap-1">
                  <span className="inline-block w-3 h-3 rounded-full" style={{ background: COLORE_NEUTRO }} /> senza importo fatturato
                </span>
                <span className="flex items-center gap-1">
                  <span className="inline-block w-3 h-3 rounded-full bg-gray-900" /> sede
                </span>
                <span>Il cerchio cresce con il numero di lavorazioni. Clicca un punto per il dettaglio.</span>
              </div>
            </Card>

            {luogoAttivo && (
              <Card className="shadow-sm p-5">
                <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-800">{luogoAttivo.cliente}</h2>
                    <p className="text-sm text-gray-500">{luogoAttivo.place}</p>
                  </div>
                  <div className="text-sm text-gray-700 text-right">
                    <div>
                      carburante {euro(luogoAttivo.carburante)} · usura {euro(luogoAttivo.usura)} · manodopera{' '}
                      {euro(luogoAttivo.manodopera)}
                    </div>
                    <div>
                      totale costi <strong>{euro(luogoAttivo.costo)}</strong> · fatturato <strong>{euro(luogoAttivo.ricavo)}</strong>{' '}
                      · margine <Margine valore={luogoAttivo.margine} />
                    </div>
                  </div>
                </div>
                <TabellaLavorazioni righe={luogoAttivo.lavorazioni} onModifica={setInModifica} />
              </Card>
            )}

            <Card className="shadow-sm p-5">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                <div>
                  <h2 className="text-lg font-semibold text-gray-800">
                    Lavorazioni senza posizione: {senzaPosizione.length}
                  </h2>
                  <p className="text-sm text-gray-500">
                    Le coordinate si ricavano dal campo "Luogo intervento". Se il testo non viene riconosciuto,
                    inseriscile a mano con "Modifica".
                  </p>
                </div>
                <button type="button" className={btnPrimario} onClick={geocodificaMancanti} disabled={!senzaPosizione.length}>
                  Cerca le posizioni mancanti
                </button>
              </div>
              {geocodificaStato && <p className="text-sm text-gray-700 mb-3">{geocodificaStato}</p>}
              {senzaPosizione.length > 0 && <TabellaLavorazioni righe={senzaPosizione.slice(0, 50)} onModifica={setInModifica} />}
              {senzaPosizione.length > 50 && (
                <p className="text-xs text-gray-500 mt-2">Mostrate le prime 50.</p>
              )}
            </Card>
          </div>
        )}

        {/* ================= CLIENTI ================= */}
        {tab === 'clienti' && (
          <Card className="shadow-sm p-5">
            <h2 className="text-lg font-semibold text-gray-800 mb-1">Clienti, dal peggiore al migliore</h2>
            <p className="text-sm text-gray-500 mb-4">
              Il margine e' fatturato meno costi di trasferta, calcolato solo sulle lavorazioni che hanno un importo
              in fatturazione. Un margine negativo su un cliente ricorrente e' il segnale per rivedere l'offerta.
            </p>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <Th>Cliente</Th>
                    <Th className="text-right">Lavorazioni</Th>
                    <Th className="text-right">Ore</Th>
                    <Th className="text-right">Km</Th>
                    <Th className="text-right">Carburante</Th>
                    <Th className="text-right">Usura</Th>
                    <Th className="text-right">Manodopera</Th>
                    <Th className="text-right">Costi</Th>
                    <Th className="text-right">Fatturato</Th>
                    <Th className="text-right">Margine</Th>
                    <Th className="text-right">Margine %</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {clienti.map((c) => (
                    <tr key={c.cliente} className="hover:bg-gray-50">
                      <Td className="font-medium max-w-[18rem] truncate">{c.cliente}</Td>
                      <Td className="text-right">
                        {c.n}
                        {c.conRicavo < c.n ? <span className="text-gray-400" title="lavorazioni senza importo"> ({c.n - c.conRicavo} s.i.)</span> : null}
                      </Td>
                      <Td className="text-right">{num(c.ore)}</Td>
                      <Td className="text-right">{num(c.km, 0)}</Td>
                      <Td className="text-right">{euro(c.carburante)}</Td>
                      <Td className="text-right">{euro(c.usura)}</Td>
                      <Td className="text-right">{euro(c.manodopera)}</Td>
                      <Td className="text-right">{euro(c.costo)}</Td>
                      <Td className="text-right">{euro(c.ricavo)}</Td>
                      <Td className="text-right">
                        <Margine valore={c.margine} />
                      </Td>
                      <Td className="text-right">{c.ricavo ? `${num((c.margine / c.ricavo) * 100, 0)} %` : '—'}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-500 mt-2">s.i. = senza importo in fatturazione (i costi ci sono, il ricavo no).</p>
          </Card>
        )}

        {/* ================= TECNICI ================= */}
        {tab === 'tecnici' && (
          <div className="space-y-4">
            <Card className="shadow-sm p-5">
              <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-800">Classifica del mese</h2>
                  <p className="text-sm text-gray-500">
                    Ore dal primo inizio all'ultima fine di ogni uscita: cinque verbali nello stesso posto contano una
                    volta sola.
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600">Mese</label>
                  <select value={meseTecnici} onChange={(e) => setMeseTecnici(e.target.value)} className={inputCls}>
                    {chiaviMesi.map((m) => (
                      <option key={m} value={m}>
                        {etichettaMese(m)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {!(mesi[meseTecnici] || []).length ? (
                <p className="text-sm text-gray-500">Nessuna uscita registrata.</p>
              ) : (
                <div className="grid lg:grid-cols-2 gap-4">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <Th>#</Th>
                          <Th>Tecnico</Th>
                          <Th className="text-right">Ore</Th>
                          <Th className="text-right">Rapportini</Th>
                          <Th className="text-right">Uscite</Th>
                          <Th className="text-right">Km</Th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {mesi[meseTecnici].map((r, i) => (
                          <tr key={r.tecnicoId} className={i === 0 && r.ore > 0 ? 'bg-emerald-50' : ''}>
                            <Td>{i + 1}</Td>
                            <Td className="font-medium">
                              {r.nome}
                              {i === 0 && r.ore > 0 && (
                                <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-emerald-600 text-white">
                                  Tecnico del mese
                                </span>
                              )}
                            </Td>
                            <Td className="text-right">
                              {num(r.ore)}
                              {r.senzaOrari ? (
                                <span className="text-gray-400" title="uscite senza orari sul verbale"> (+{r.senzaOrari} s.o.)</span>
                              ) : null}
                            </Td>
                            <Td className="text-right">{r.rapportini}</Td>
                            <Td className="text-right">{r.uscite}</Td>
                            <Td className="text-right">{num(r.km, 0)}</Td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <p className="text-xs text-gray-500 mt-2">s.o. = uscite senza orari: contano nei rapportini ma non nelle ore.</p>
                  </div>

                  <div style={{ height: 280 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={mesi[meseTecnici]} layout="vertical" margin={{ left: 8, right: 24, top: 8, bottom: 8 }}>
                        <CartesianGrid stroke="#e5e7eb" horizontal={false} />
                        <XAxis type="number" tick={{ fontSize: 12, fill: '#6b7280' }} axisLine={false} tickLine={false} unit=" h" />
                        <YAxis type="category" dataKey="nome" width={120} tick={{ fontSize: 12, fill: '#374151' }} axisLine={false} tickLine={false} />
                        <Tooltip formatter={(v) => [`${num(v)} h`, 'Ore']} cursor={{ fill: '#f3f4f6' }} />
                        <Bar dataKey="ore" radius={[0, 4, 4, 0]} barSize={18}>
                          {mesi[meseTecnici].map((r, i) => (
                            <Cell key={r.tecnicoId} fill={i === 0 ? COLORE_SERIE : '#86b6ef'} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </Card>

            <Card className="shadow-sm p-5">
              <h2 className="text-lg font-semibold text-gray-800 mb-3">Totali del periodo</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <Th>Tecnico</Th>
                      <Th className="text-right">Ore</Th>
                      <Th className="text-right">Rapportini</Th>
                      <Th className="text-right">Uscite</Th>
                      <Th className="text-right">Km</Th>
                      <Th className="text-right">Mesi vinti</Th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {classificaAnno.map((r) => (
                      <tr key={r.tecnicoId}>
                        <Td className="font-medium">{r.nome}</Td>
                        <Td className="text-right">{num(r.ore)}</Td>
                        <Td className="text-right">{r.rapportini}</Td>
                        <Td className="text-right">{r.uscite}</Td>
                        <Td className="text-right">{num(r.km, 0)}</Td>
                        <Td className="text-right">{r.mesiVinti}</Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        )}

        {/* ================= LAVORAZIONI ================= */}
        {tab === 'lavorazioni' && (
          <Card className="shadow-sm p-5">
            <h2 className="text-lg font-semibold text-gray-800 mb-3">Tutte le lavorazioni del periodo</h2>
            <TabellaLavorazioni
              righe={[...lavorazioni].sort((a, b) => new Date(b.dataIntervento || 0) - new Date(a.dataIntervento || 0))}
              onModifica={setInModifica}
            />
          </Card>
        )}

        {/* ================= IMPOSTAZIONI ================= */}
        {tab === 'impostazioni' && dati && <Impostazioni impostazioni={dati.impostazioni} veicoli={dati.veicoli} onCambiato={carica} />}
      </div>

      {inModifica && (
        <EditorTrasferta
          lavorazione={inModifica}
          veicoli={dati?.veicoli || []}
          tecnici={dati?.tecnici || []}
          onChiudi={() => setInModifica(null)}
          onSalvato={dopoSalvataggio}
        />
      )}
    </div>
  );
}

// ============================================================
//  IMPOSTAZIONI: sede, costo orario, veicoli, prezzi carburante
// ============================================================

function Impostazioni({ impostazioni, veicoli, onCambiato }) {
  const [form, setForm] = useState(() => ({
    indirizzo: impostazioni?.sede?.indirizzo || '',
    lat: impostazioni?.sede?.lat ?? '',
    lng: impostazioni?.sede?.lng ?? '',
    costoOrarioTecnico: impostazioni?.costoOrarioTecnico ?? 30,
    fattoreStrada: impostazioni?.fattoreStrada ?? 1.3,
    prezzoLitroDefault: impostazioni?.prezzoLitroDefault ?? 1.75,
    consumoDefaultLitri100km: impostazioni?.consumoDefaultLitri100km ?? 8,
    costoUsuraDefaultKm: impostazioni?.costoUsuraDefaultKm ?? 0.2,
    veicoloDefaultId: impostazioni?.veicoloDefaultId || '',
  }));
  const [msg, setMsg] = useState('');
  const [prezzi, setPrezzi] = useState([]);
  const [nuovoPrezzo, setNuovoPrezzo] = useState({ carburante: 'diesel', dal: dataIso(new Date()), prezzoLitro: '' });
  const [nuovoVeicolo, setNuovoVeicolo] = useState({ nome: '', targa: '', carburante: 'diesel', consumoLitri100km: 8, costoUsuraKm: 0.2 });
  const [veicoloInModifica, setVeicoloInModifica] = useState(null);

  const cambia = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const caricaPrezzi = async () => {
    try {
      setPrezzi(await chiamata(`${API_BASE}/prezzi-carburante`));
    } catch (err) {
      setMsg(err.message);
    }
  };
  useEffect(() => {
    caricaPrezzi();
  }, []);

  const esegui = async (fn, okMsg) => {
    setMsg('');
    try {
      await fn();
      if (okMsg) setMsg(okMsg);
    } catch (err) {
      setMsg(`Errore: ${err.message}`);
    }
  };

  const salvaImpostazioni = () =>
    esegui(async () => {
      await chiamata(
        `${API_BASE}/impostazioni-costi`,
        json(
          {
            sede: { indirizzo: form.indirizzo, lat: form.lat, lng: form.lng },
            costoOrarioTecnico: form.costoOrarioTecnico,
            fattoreStrada: form.fattoreStrada,
            prezzoLitroDefault: form.prezzoLitroDefault,
            consumoDefaultLitri100km: form.consumoDefaultLitri100km,
            costoUsuraDefaultKm: form.costoUsuraDefaultKm,
            veicoloDefaultId: form.veicoloDefaultId || null,
          },
          'PUT'
        )
      );
      await onCambiato();
    }, 'Impostazioni salvate.');

  const trovaSede = () =>
    esegui(async () => {
      const pos = await chiamata(`${API_BASE}/geocodifica`, json({ indirizzo: form.indirizzo }));
      setForm((p) => ({ ...p, lat: pos.lat, lng: pos.lng }));
    }, 'Coordinate trovate: ricordati di salvare.');

  const aggiungiPrezzo = () =>
    esegui(async () => {
      await chiamata(`${API_BASE}/prezzi-carburante`, json(nuovoPrezzo));
      setNuovoPrezzo((p) => ({ ...p, prezzoLitro: '' }));
      await caricaPrezzi();
      await onCambiato();
    });

  const eliminaPrezzo = (id) =>
    esegui(async () => {
      await chiamata(`${API_BASE}/prezzi-carburante/${id}`, { method: 'DELETE' });
      await caricaPrezzi();
      await onCambiato();
    });

  const aggiungiVeicolo = () =>
    esegui(async () => {
      await chiamata(`${API_BASE}/veicoli`, json(nuovoVeicolo));
      setNuovoVeicolo({ nome: '', targa: '', carburante: 'diesel', consumoLitri100km: 8, costoUsuraKm: 0.2 });
      await onCambiato();
    });

  const salvaVeicolo = () =>
    esegui(async () => {
      const { _id, ...resto } = veicoloInModifica;
      await chiamata(`${API_BASE}/veicoli/${_id}`, json(resto, 'PUT'));
      setVeicoloInModifica(null);
      await onCambiato();
    });

  const archiviaVeicolo = (v) =>
    esegui(async () => {
      await chiamata(`${API_BASE}/veicoli/${v._id}`, json({ attivo: !v.attivo }, 'PUT'));
      await onCambiato();
    });

  const cambiaVeicolo = (e) => setVeicoloInModifica((p) => ({ ...p, [e.target.name]: e.target.value }));
  const cambiaNuovoVeicolo = (e) => setNuovoVeicolo((p) => ({ ...p, [e.target.name]: e.target.value }));

  return (
    <div className="space-y-4">
      {msg && <div className="rounded-lg bg-blue-50 border border-blue-200 text-blue-800 px-4 py-2 text-sm">{msg}</div>}

      <Card className="shadow-sm p-5">
        <h2 className="text-lg font-semibold text-gray-800 mb-1">Sede e parametri di calcolo</h2>
        <p className="text-sm text-gray-500 mb-4">
          Le trasferte partono dalla sede. Quando il tecnico non scrive i km, si stima: distanza in linea d'aria ×
          fattore strada × 2 (andata e ritorno).
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="col-span-2">
            <label className="block text-xs font-medium text-gray-600">Indirizzo sede</label>
            <input name="indirizzo" value={form.indirizzo} onChange={cambia} className={inputCls} placeholder="Via ..., Citta (PR)" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600">Latitudine</label>
            <input name="lat" value={form.lat} onChange={cambia} className={inputCls} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600">Longitudine</label>
            <input name="lng" value={form.lng} onChange={cambia} className={inputCls} />
          </div>
          <div className="col-span-2 md:col-span-4">
            <button type="button" className={btnSecondario} onClick={trovaSede} disabled={!form.indirizzo}>
              Trova coordinate dall'indirizzo
            </button>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600">Costo orario tecnico (€/h)</label>
            <input type="number" step="0.5" name="costoOrarioTecnico" value={form.costoOrarioTecnico} onChange={cambia} className={inputCls} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600">Fattore strada</label>
            <input type="number" step="0.05" name="fattoreStrada" value={form.fattoreStrada} onChange={cambia} className={inputCls} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600">Prezzo litro se manca (€)</label>
            <input type="number" step="0.001" name="prezzoLitroDefault" value={form.prezzoLitroDefault} onChange={cambia} className={inputCls} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600">Veicolo predefinito</label>
            <select name="veicoloDefaultId" value={form.veicoloDefaultId} onChange={cambia} className={inputCls}>
              <option value="">— usa i valori sotto —</option>
              {veicoli.filter((v) => v.attivo).map((v) => (
                <option key={v._id} value={v._id}>
                  {v.nome}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600">Consumo default (l/100 km)</label>
            <input type="number" step="0.1" name="consumoDefaultLitri100km" value={form.consumoDefaultLitri100km} onChange={cambia} className={inputCls} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600">Usura default (€/km)</label>
            <input type="number" step="0.01" name="costoUsuraDefaultKm" value={form.costoUsuraDefaultKm} onChange={cambia} className={inputCls} />
          </div>
        </div>
        <div className="mt-4">
          <button type="button" className={btnPrimario} onClick={salvaImpostazioni}>
            Salva impostazioni
          </button>
        </div>
      </Card>

      <Card className="shadow-sm p-5">
        <h2 className="text-lg font-semibold text-gray-800 mb-1">Furgoni e auto</h2>
        <p className="text-sm text-gray-500 mb-4">
          Il costo di usura per km (gomme, tagliandi, ammortamento, assicurazione) si prende dalle tabelle ACI del
          modello, escludendo il carburante che viene calcolato a parte.
        </p>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <Th>Nome</Th>
                <Th>Targa</Th>
                <Th>Carburante</Th>
                <Th className="text-right">l/100 km</Th>
                <Th className="text-right">Usura €/km</Th>
                <Th>Stato</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {veicoli.map((v) =>
                veicoloInModifica && veicoloInModifica._id === v._id ? (
                  <tr key={v._id} className="bg-gray-50">
                    <Td><input name="nome" value={veicoloInModifica.nome} onChange={cambiaVeicolo} className={inputCls} /></Td>
                    <Td><input name="targa" value={veicoloInModifica.targa} onChange={cambiaVeicolo} className={inputCls} /></Td>
                    <Td>
                      <select name="carburante" value={veicoloInModifica.carburante} onChange={cambiaVeicolo} className={inputCls}>
                        {CARBURANTI.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </Td>
                    <Td><input type="number" step="0.1" name="consumoLitri100km" value={veicoloInModifica.consumoLitri100km} onChange={cambiaVeicolo} className={inputCls} /></Td>
                    <Td><input type="number" step="0.01" name="costoUsuraKm" value={veicoloInModifica.costoUsuraKm} onChange={cambiaVeicolo} className={inputCls} /></Td>
                    <Td>{v.attivo ? 'attivo' : 'archiviato'}</Td>
                    <Td>
                      <div className="flex gap-2">
                        <button type="button" className={btnPrimario} onClick={salvaVeicolo}>Salva</button>
                        <button type="button" className={btnSecondario} onClick={() => setVeicoloInModifica(null)}>Annulla</button>
                      </div>
                    </Td>
                  </tr>
                ) : (
                  <tr key={v._id} className={v.attivo ? '' : 'text-gray-400'}>
                    <Td className="font-medium">{v.nome}</Td>
                    <Td>{v.targa || '—'}</Td>
                    <Td>{v.carburante}</Td>
                    <Td className="text-right">{num(v.consumoLitri100km)}</Td>
                    <Td className="text-right">{num(v.costoUsuraKm, 3)}</Td>
                    <Td>{v.attivo ? 'attivo' : 'archiviato'}</Td>
                    <Td>
                      <div className="flex gap-2">
                        <button type="button" className={btnSecondario} onClick={() => setVeicoloInModifica({ _id: v._id, nome: v.nome, targa: v.targa || '', carburante: v.carburante, consumoLitri100km: v.consumoLitri100km, costoUsuraKm: v.costoUsuraKm })}>
                          Modifica
                        </button>
                        <button type="button" className={btnSecondario} onClick={() => archiviaVeicolo(v)}>
                          {v.attivo ? 'Archivia' : 'Riattiva'}
                        </button>
                      </div>
                    </Td>
                  </tr>
                )
              )}
              <tr className="bg-emerald-50/40">
                <Td><input name="nome" value={nuovoVeicolo.nome} onChange={cambiaNuovoVeicolo} className={inputCls} placeholder="Nuovo veicolo" /></Td>
                <Td><input name="targa" value={nuovoVeicolo.targa} onChange={cambiaNuovoVeicolo} className={inputCls} placeholder="Targa" /></Td>
                <Td>
                  <select name="carburante" value={nuovoVeicolo.carburante} onChange={cambiaNuovoVeicolo} className={inputCls}>
                    {CARBURANTI.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </Td>
                <Td><input type="number" step="0.1" name="consumoLitri100km" value={nuovoVeicolo.consumoLitri100km} onChange={cambiaNuovoVeicolo} className={inputCls} /></Td>
                <Td><input type="number" step="0.01" name="costoUsuraKm" value={nuovoVeicolo.costoUsuraKm} onChange={cambiaNuovoVeicolo} className={inputCls} /></Td>
                <Td></Td>
                <Td>
                  <button type="button" className={btnPrimario} onClick={aggiungiVeicolo} disabled={!nuovoVeicolo.nome.trim()}>
                    Aggiungi
                  </button>
                </Td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>

      <Card className="shadow-sm p-5">
        <h2 className="text-lg font-semibold text-gray-800 mb-1">Prezzi del carburante</h2>
        <p className="text-sm text-gray-500 mb-4">
          Ogni riga vale da quella data in poi. Per ogni uscita si usa il prezzo in vigore il giorno dell'intervento.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 items-end mb-4">
          <div>
            <label className="block text-xs font-medium text-gray-600">Carburante</label>
            <select value={nuovoPrezzo.carburante} onChange={(e) => setNuovoPrezzo((p) => ({ ...p, carburante: e.target.value }))} className={inputCls}>
              {CARBURANTI.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600">Dal</label>
            <input type="date" value={nuovoPrezzo.dal} onChange={(e) => setNuovoPrezzo((p) => ({ ...p, dal: e.target.value }))} className={inputCls} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600">€/litro</label>
            <input type="number" step="0.001" value={nuovoPrezzo.prezzoLitro} onChange={(e) => setNuovoPrezzo((p) => ({ ...p, prezzoLitro: e.target.value }))} className={inputCls} placeholder="1.749" />
          </div>
          <div>
            <button type="button" className={btnPrimario} onClick={aggiungiPrezzo} disabled={!nuovoPrezzo.prezzoLitro || !nuovoPrezzo.dal}>
              Aggiungi prezzo
            </button>
          </div>
        </div>
        {!prezzi.length ? (
          <p className="text-sm text-gray-500">Nessun prezzo registrato: si usa il prezzo di default delle impostazioni.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <Th>Dal</Th>
                  <Th>Carburante</Th>
                  <Th className="text-right">€/litro</Th>
                  <Th></Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {prezzi.map((p) => (
                  <tr key={p._id}>
                    <Td>{dataIt(p.dal)}</Td>
                    <Td>{p.carburante}</Td>
                    <Td className="text-right">{num(p.prezzoLitro, 3)}</Td>
                    <Td>
                      <button type="button" className={btnSecondario} onClick={() => eliminaPrezzo(p._id)}>
                        Elimina
                      </button>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
